import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import MainLogo from "../../assets/images/AstraLogo.svg";
import { resetPassword } from "../../shared/_services/auth.services";
import { clearResetToken, getResetToken } from "../../shared/_helpers/otpChallenge";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const fromPage = location.state?.from;
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'))?.user;
    if (!user && !fromPage) {
      navigate('/login');
    }
  }, []);

  const handleResetPassword = async (values) => {
    let email = localStorage.getItem("resetPasswordEmail");

    // Backend SEC-084: this route now demands the single-use reset token, which
    // /otp-verify minted when the code was redeemed with
    // `purpose: "reset_password"`. Reaching this screen used to be proof enough
    // of the OTP step — it was enforced only by this app's navigation, so an
    // anonymous caller who knew an email address could set that account's
    // password. Without the token the request is refused, so bounce back to the
    // start of the flow rather than sending one that cannot succeed.
    const token = getResetToken();
    if (!token) {
      enqueueSnackbar("This password reset link has expired. Please request a new code.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate("/forgot-password");
      return;
    }

    const data = await resetPassword({ email: email, password: values.newPassword, token });
    if (data.status === 200) {
      enqueueSnackbar(data?.data?.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      // Consumed server-side on success; drop our copy with the email.
      clearResetToken();
      window.localStorage.removeItem("resetPasswordEmail")
      navigate("/login");
    }
    else {
      enqueueSnackbar(
        data.data?.message || data.response?.data?.message || "Password Reset Failed.",
        { variant: "error" }
      );
    }
  };

  const initialValues = {
    newPassword: "",
    confirmNewPassword: "",
  };

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
        "Password should contain at least 1 number, 1 lowercase, 1 uppercase, 1 special character"
      ),
    confirmNewPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  return (
    <div className="login-signup-wrap forget-wrap">
      <div className="container">
        <div className="row">
          <div className="col-md-12 col-lg-6 logo-wrap place-content-center">
            <a href="/">
              <img className="login-signup-logo" src={MainLogo} alt="Main Logo" />
            </a>
            <div className="auth-page-content">
              <h2>
                Reset Your Password
              </h2>
              <p>
                Enter your new password below to securely update your account and regain access to your financial dashboard.
              </p>
              <p className="w-100">
                <svg width="46" height="3" viewBox="0 0 46 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.5H46" stroke="white" strokeWidth="2" />
                </svg>
                &nbsp;&nbsp;&nbsp;
                PROFESSIONAL FINANCIAL ADVISORY</p>
            </div>
          </div>
          <div className="col-md-12 col-lg-6">
            <div className="login-signup-box signup-box">
              <h2 className="login-signup-head text-left mt-2 mb-5">Reset Password</h2>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    await handleResetPassword(values);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, touched, errors }) => (
                  <Form>
                    <div className="form-group password-field">
                      <Field
                        type={showPassword ? "text" : "password"}
                        id="newPassword"
                        name="newPassword"
                        placeholder="Enter New Password"
                        className={`form-control ${touched.newPassword && errors.newPassword
                          ? "is-invalid"
                          : ""
                          }`}
                      />
                      <ErrorMessage
                        name="newPassword"
                        component="div"
                        className="invalid-feedback"
                      />
                      <span
                        className="toggle-password"
                        onClick={() => { setShowPassword(!showPassword) }}
                      >
                        {showPassword ? (
                          <i className="iconoir-eye-closed"></i>
                        ) : (
                          <i className="iconoir-eye"></i>
                        )}
                      </span>
                    </div>
                    <div className="form-group password-field">
                      <Field
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        placeholder="Re-enter New Password"
                        className={`form-control ${touched.confirmNewPassword && errors.confirmNewPassword
                          ? "is-invalid"
                          : ""
                          }`}
                      />
                      <ErrorMessage
                        name="confirmNewPassword"
                        component="div"
                        className="invalid-feedback"
                      />
                      <span
                        className="toggle-password"
                        onClick={() => { setShowConfirmPassword(!showConfirmPassword) }}
                      >
                        {showConfirmPassword ? (
                          <i className="iconoir-eye-closed"></i>
                        ) : (
                          <i className="iconoir-eye"></i>
                        )}
                      </span>
                    </div>
                    <button
                      type="submit"
                      className="auth-screen-btn mt-2 mb-2"
                      disabled={isSubmitting}
                    >
                       {isSubmitting ? "Submitting..." : "Reset Password"}
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
